
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Check, Share } from 'lucide-react';
import { toast } from 'sonner';
import { useInviteCodeActions } from '../utils/inviteCodeUtils';

interface InviteCodeEditorProps {
  userId?: string | undefined;
  inviteCode?: string | null;
  isEditable: boolean;
  isFamilyAdmin?: boolean;
}

export const InviteCodeEditor: React.FC<InviteCodeEditorProps> = ({
  userId,
  inviteCode,
  isEditable,
  isFamilyAdmin = false
}) => {
  const { codeCopied, isSharing, copyInviteCode, handleShareInviteCode, canShare } = useInviteCodeActions(inviteCode);

  // If not editable or no user, just show the code
  if (!isEditable || !userId) {
    return inviteCode ? (
      <div className="font-mono bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded text-sm">
        {inviteCode}
      </div>
    ) : (
      <div className="text-muted-foreground italic">No invite code available</div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center space-x-2">
        <code className="bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded font-mono text-sm flex-grow">
          {inviteCode || 'No invite code'}
        </code>
        
        <div className="flex gap-1">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={copyInviteCode}
            className="h-8 w-8 p-0"
            title="Copy invite code"
            disabled={!inviteCode}
          >
            {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
          
          {canShare && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShareInviteCode}
              className="h-8 w-8 p-0"
              title="Share invite code"
              disabled={!inviteCode || isSharing}
            >
              <Share className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      <p className="text-xs text-muted-foreground">
        {isFamilyAdmin ? (
          "Share this code with anyone you want to join your family circle."
        ) : (
          "This is your family circle's invite code. Share it with others to invite them to join."
        )}
      </p>
    </div>
  );
};
